<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue'
import IconClose from '@/Components/Icons/IconClose.vue'
import BaseIcon from '@/Components/Shared/BaseIcon.vue'

const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'bg-white',
    },
    show: {
        type: Boolean,
        default: false,
    },
    maxWidth: {
        type: String,
        default: '2xl',
    },
    height: {
        type: String,
        default: 'auto',
    },
    closeable: {
        type: Boolean,
        default: true,
    },
})

const emit = defineEmits(['close'])

watch(
    () => props.show,
    () => {
        if (props.show) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = null
        }
    }
)

const close = () => {
    if (props.closeable) {
        emit('close')
    }
}

const closeOnEscape = (e) => {
    if (e.key === 'Escape' && props.show) {
        close()
    }
}

onMounted(() => document.addEventListener('keydown', closeOnEscape))

onUnmounted(() => {
    document.removeEventListener('keydown', closeOnEscape)
    document.body.style.overflow = null
})

const maxWidthClass = computed(() => {
    return {
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-md',
        lg: 'sm:max-w-lg',
        xl: 'sm:max-w-xl',
        '2xl': 'max-w-[800px]',
        80: 'w-[80%]',
        600: 'max-w-[600px]',
        1000: 'max-w-[1000px]',
        1400: 'max-w-[1400px]',
    }[props.maxWidth]
})

const heightClass = computed(() => {
    return {
        90: 'h-[90vh]',
    }[props.height]
})
</script>

<template>
    <Teleport to="body">
        <Transition leave-active-class="duration-200">
            <div
                v-show="show"
                class="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-0 z-50 flex justify-center items-center"
                scroll-region
            >
                <Transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="show" class="fixed inset-0 transform transition-all" @click="close">
                        <div class="absolute inset-0 bg-black opacity-60"/>
                    </div>
                </Transition>

                <Transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="show"
                        class="rounded-2xl overflow-hidden shadow-xl transform transition-all p-10 relative"
                        :class="[maxWidthClass, backgroundColor,heightClass]"
                    >
                        <div class="absolute right-0 top-1 m-10 text-2xl cursor-pointer z-50">
                            <base-icon
                                v-if="closeable"
                                :height="'24px'"
                                :is-expanded="true"
                                :width="'24px'"
                                v-on:click="close()"
                            >
                                <IconClose color="#000000"></IconClose>
                            </base-icon>
                        </div>
                        <slot v-if="show"/>
                    </div>
                </Transition>
            </div>
        </Transition>
    </Teleport>
</template>
<style lang="scss">
.m-modal-paragraph {
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
    letter-spacing: 0.5px;
    text-align: left;
    color: #ffb527;
    padding-left: 26px;

    > .m-modal-paragraph-bold {
        font-size: 14px;
        font-weight: 600;
        line-height: 140%;
        letter-spacing: 0.5px;
        text-align: left;
    }
}
</style>
